import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { removeSelectedItem, replaceStatusChangedItem } from '@/helpers/CRUD';
import { extractError } from '@/helpers/extractApiError';
import CompanyService from '@/services/CustomersServices/CompanyService';
import { deleteCompany } from '../delete/deleteSlice';

const model = {
    data: [],
    totalItems: 0,
    loading: false,
    error: null,
    successOnCompanyStatusChange: '',
    errorOnCompanyStatusChange: null,
    mismatch_file: null,
    successOnCompanyUpload: '',
    errorOnCompanyUpload: null,
    xlsxUrl: null,
};

export const getCompanyList = createAsyncThunk(
    'company/getCompanyList',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CompanyService.getCompanyList(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const changeCompanyStatus = createAsyncThunk(
    'company/changeCompanyStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CompanyService.changeCompanyStatus(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const downloadCompanyData = createAsyncThunk(
    'company/downloadCompanyData',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CompanyService.downloadCompanyData(data);
            const { file_url: xlsxUrl } = response.data;
            return xlsxUrl;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const uploadCompanyData = createAsyncThunk(
    'company/uploadCompanyData',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await CompanyService.uploadCompanyData(formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const companyListSlice = createSlice({
    name: 'companyList',
    initialState: model,
    reducers: {
        resetChangeCompanyStatusStateOnSuccess: (state) => {
            state.successOnCompanyStatusChange = '';
        },
        resetChangeCompanyStatusStateOnError: (state) => {
            state.error = null;
        },
        resetCompanyUploadStateOnSuccess: (state) => {
            state.successOnCompanyUpload = '';
        },
        resetCompanyUploadStateOnError: (state) => {
            state.errorOnCompanyUpload = null;
        },
        resetCompanyDownloadState: (state) => {
            state.xlsxUrl = null;
        },
    },
    extraReducers: (builder) => {
        // company list
        builder.addCase(getCompanyList.pending, (state) => {
            state.loading = true;
        }).addCase(getCompanyList.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.companies;
            state.totalItems = action.payload.total;
        }).addCase(getCompanyList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change company status
        builder.addCase(changeCompanyStatus.pending, (state) => {
            state.loading = true;
        }).addCase(changeCompanyStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.successOnCompanyStatusChange = 'Company status changed successfully!';
            state.data = replaceStatusChangedItem(
                state.data,
                action.payload.id,
                action.payload.status,
            );
        }).addCase(changeCompanyStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Handle deleting company
        builder.addCase(deleteCompany.fulfilled, (state, action) => {
            state.data = removeSelectedItem(state.data, action.payload.companyId);
            state.totalItems -= 1;
        });

        // download company data
        builder.addCase(downloadCompanyData.pending, (state) => {
            state.loading = true;
        }).addCase(downloadCompanyData.fulfilled, (state, action) => {
            state.loading = false;
            state.xlsxUrl = action.payload;
            if (action.meta.arg.download_type === 'mismatch') {
                state.mismatch_file = null;
            }
        }).addCase(downloadCompanyData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // upload company data
        builder.addCase(uploadCompanyData.pending, (state) => {
            state.loading = true;
        }).addCase(uploadCompanyData.fulfilled, (state, action) => {
            state.loading = false;
            state.mismatch_file = action.payload.mismatch_file;
            state.successOnCompanyUpload = action.payload.mismatch_file === null
                ? 'All data processed successfully!'
                : `${action.payload.updated_rows} ${action.payload.updated_rows === 0 || action.payload.updated_rows === 1 ? 'company' : 'companies'} updated!\n`
                + `${action.payload.created_rows} ${action.payload.created_rows === 0 || action.payload.created_rows === 1 ? 'company' : 'companies'} created!\n`
                + `${action.payload.mismatch_rows} ${action.payload.mismatch_rows === 0 || action.payload.mismatch_rows === 1 ? 'company was' : 'companies were'} not updated due to mismatch!`;
        }).addCase(uploadCompanyData.rejected, (state, action) => {
            state.loading = false;
            state.errorOnCompanyUpload = action.payload;
        });
    },
});

export const {
    resetChangeCompanyStatusStateOnSuccess,
    resetChangeCompanyStatusStateOnError,
    resetCompanyUploadStateOnSuccess,
    resetCompanyUploadStateOnError,
    resetCompanyDownloadState,
} = companyListSlice.actions;

export default companyListSlice.reducer;
